import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Card,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  LinearProgress,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import {
  addDoc,
  collection,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { auth, db, storage } from "../firebaseConfig";
import AdminNavbar, { AdminTopBar } from "../components/AdminNavbar";
import { useAdminData } from "../context/AdminDataContext";
import { deleteKnowledgeBaseDocument } from "../services/knowledgeBaseService";

const ACCEPTED_TYPES = "application/pdf,image/*";
const MAX_DOCUMENT_FILES = 150;
const isPdf = (file) => file?.type === "application/pdf";
const isImage = (file) => Boolean(file?.type?.startsWith("image/"));

const STATUS_STYLES = {
  queued: { bg: "#eef1f6", fg: "#64748b", label: "En attente" },
  processing: { bg: "#fff3e0", fg: "#b45309", label: "Traitement en cours" },
  ready: { bg: "#e7f7ec", fg: "#16a34a", label: "Prêt" },
  failed: { bg: "#fde8e8", fg: "#dc2626", label: "Échec" },
};

function validateFiles(files) {
  if (!files.length) return "Ajoutez un PDF ou une/plusieurs images.";
  const pdfCount = files.filter(isPdf).length;
  const imageCount = files.filter(isImage).length;
  const otherCount = files.length - pdfCount - imageCount;
  if (otherCount > 0) return "Seuls les PDF et les images sont acceptés.";
  if (pdfCount > 0 && imageCount > 0) {
    return "Choisissez soit un seul PDF, soit une ou plusieurs images — pas les deux.";
  }
  if (pdfCount > 1) return "Un seul fichier PDF à la fois.";
  if (imageCount > MAX_DOCUMENT_FILES) {
    return `Maximum ${MAX_DOCUMENT_FILES} images par document.`;
  }
  return null;
}

function FilePreviewList({ files, onRemove }) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
      {files.map((file, index) => {
        const objectUrl = isImage(file) ? URL.createObjectURL(file) : null;
        return (
          <Box
            key={`${file.name}-${index}`}
            sx={{
              position: "relative",
              width: 72,
              height: 72,
              borderRadius: 1.5,
              border: "1px solid #e5e7eb",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#f8fafc",
            }}
          >
            {objectUrl ? (
              <Box
                component="img"
                src={objectUrl}
                onLoad={() => URL.revokeObjectURL(objectUrl)}
                alt={file.name}
                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, px: 0.5 }}>
                <InsertDriveFileOutlinedIcon sx={{ fontSize: 22, color: "#64748b" }} />
                <Typography sx={{ fontSize: 9, color: "#64748b", textAlign: "center", wordBreak: "break-all" }}>
                  {file.name}
                </Typography>
              </Box>
            )}
            <IconButton
              size="small"
              onClick={() => onRemove(index)}
              sx={{
                position: "absolute",
                top: 2,
                right: 2,
                bgcolor: "rgba(15,23,42,0.6)",
                color: "#fff",
                width: 20,
                height: 20,
                "&:hover": { bgcolor: "rgba(15,23,42,0.8)" },
              }}
            >
              <CloseIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Box>
        );
      })}
    </Box>
  );
}

export default function AdminKnowledgeBase() {
  const { teachers } = useAdminData();
  const [selectedTeacherId, setSelectedTeacherId] = useState("");
  const [title, setTitle] = useState("");
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [documents, setDocuments] = useState([]);
  const [deletingId, setDeletingId] = useState("");
  const [deleteError, setDeleteError] = useState("");

  const sortedTeachers = useMemo(
    () =>
      [...teachers].sort((a, b) =>
        `${a.firstName || ""} ${a.lastName || ""}`.localeCompare(
          `${b.firstName || ""} ${b.lastName || ""}`
        )
      ),
    [teachers]
  );

  useEffect(() => {
    if (!selectedTeacherId) {
      setDocuments([]);
      return;
    }
    const q = query(
      collection(db, "knowledgeBases", selectedTeacherId, "documents"),
      orderBy("ingestedAt", "desc")
    );
    const unsub = onSnapshot(q, (snap) => {
      setDocuments(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [selectedTeacherId]);

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteDocument = async (docId, docTitle) => {
    if (!window.confirm(`Supprimer définitivement "${docTitle}" de la base de connaissances ?`)) {
      return;
    }
    setDeleteError("");
    setDeletingId(docId);
    try {
      await deleteKnowledgeBaseDocument({ teacherDocId: selectedTeacherId, docId });
    } catch (err) {
      setDeleteError(err.message || "Échec de la suppression.");
    } finally {
      setDeletingId("");
    }
  };

  const uploadFilesToStorage = (teacherDocId, pickedFiles) => {
    const progressByIndex = new Array(pickedFiles.length).fill(0);
    const reportProgress = () => {
      const total = progressByIndex.reduce((a, b) => a + b, 0) / pickedFiles.length;
      setProgress(Math.round(total));
    };

    return Promise.all(
      pickedFiles.map(
        (file, index) =>
          new Promise((resolve, reject) => {
            const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "-");
            const storageRef = ref(
              storage,
              `knowledgeBases/${teacherDocId}/${Date.now()}-${index}-${safeName}`
            );
            const uploadTask = uploadBytesResumable(storageRef, file);
            uploadTask.on(
              "state_changed",
              (snapshot) => {
                progressByIndex[index] = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                reportProgress();
              },
              reject,
              async () => {
                await getDownloadURL(uploadTask.snapshot.ref);
                resolve(uploadTask.snapshot.ref.fullPath);
              }
            );
          })
      )
    );
  };

  const handleSubmit = async () => {
    setError("");
    setSuccess("");

    if (!selectedTeacherId) {
      setError("Choisissez un prof.");
      return;
    }
    if (!title.trim()) {
      setError("Ajoutez un titre pour ce document.");
      return;
    }
    const fileError = validateFiles(files);
    if (fileError) {
      setError(fileError);
      return;
    }

    try {
      setIsSaving(true);
      setProgress(0);

      const storagePaths = await uploadFilesToStorage(selectedTeacherId, files);

      await addDoc(collection(db, "knowledgeBases", selectedTeacherId, "documents"), {
        title: title.trim(),
        storagePaths,
        originalFileName: files[0].name,
        status: "queued",
        ingestedByUid: auth.currentUser?.uid || null,
        ingestedAt: serverTimestamp(),
      });

      setTitle("");
      setFiles([]);
      setSuccess("Document envoyé pour traitement.");
    } catch (err) {
      setError(err.message || "Échec de l'envoi.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <AdminTopBar />
      <Box
        sx={{
          display: "flex",
          height: "calc(100vh - 64px)",
          mt: "64px",
          backgroundColor: "#ffffff",
          flexDirection: "row-reverse",
          overflow: "hidden",
        }}
      >
        <AdminNavbar />

        <Box component="main" sx={{ flexGrow: 1, p: { xs: 1, md: 3 }, overflowY: "auto" }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Base de connaissances (RAG)
          </Typography>

          <Card sx={{ p: 3, mt: 2, maxWidth: 640 }}>
          <Typography sx={{ fontWeight: 700, mb: 2 }}>
            Ajouter un document pédagogique
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="kb-teacher-label">Prof</InputLabel>
              <Select
                labelId="kb-teacher-label"
                label="Prof"
                value={selectedTeacherId}
                onChange={(e) => setSelectedTeacherId(e.target.value)}
              >
                {sortedTeachers.map((teacher) => (
                  <MenuItem key={teacher.id} value={teacher.id}>
                    {`${teacher.firstName || ""} ${teacher.lastName || ""}`.trim() ||
                      teacher.email ||
                      teacher.id}
                    {teacher.email ? ` — ${teacher.email}` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Titre du document"
              placeholder='ex: "SVT 3AS — La photosynthèse (chapitre complet)"'
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
            />

            <Button variant="outlined" component="label">
              {files.length ? `${files.length} fichier(s) sélectionné(s)` : "Choisir un PDF ou une/plusieurs images"}
              <input
                type="file"
                accept={ACCEPTED_TYPES}
                multiple
                hidden
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
              />
            </Button>
            <FilePreviewList files={files} onRemove={removeFile} />

            {progress > 0 && progress < 100 && (
              <LinearProgress variant="determinate" value={progress} />
            )}

            {error && <Typography sx={{ color: "red" }}>{error}</Typography>}
            {success && <Typography sx={{ color: "green" }}>{success}</Typography>}

            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSaving}
              sx={{ alignSelf: "flex-start" }}
            >
              Envoyer
            </Button>
          </Box>
        </Card>

        {selectedTeacherId && (
          <Card sx={{ p: 3, mt: 3, maxWidth: 640 }}>
            <Typography sx={{ fontWeight: 700, mb: 2 }}>
              Historique des documents
            </Typography>
            {deleteError && (
              <Typography sx={{ color: "red", fontSize: 13, mb: 1.5 }}>
                {deleteError}
              </Typography>
            )}
            {documents.length === 0 ? (
              <Typography sx={{ color: "#64748b" }}>
                Aucun document ingéré pour ce prof pour l'instant.
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {documents.map((doc) => {
                  const style = STATUS_STYLES[doc.status] || STATUS_STYLES.queued;
                  return (
                    <Box
                      key={doc.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        p: 1.5,
                        border: "1px solid #eef1f6",
                        borderRadius: 1.5,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                          {doc.title}
                        </Typography>
                        {doc.pageCount ? (
                          <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                            {doc.pageCount} page(s)
                          </Typography>
                        ) : null}
                        {doc.status === "failed" && doc.error ? (
                          <Typography sx={{ fontSize: 12, color: "#dc2626" }}>
                            {doc.error}
                          </Typography>
                        ) : null}
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                        <Chip
                          label={style.label}
                          sx={{ bgcolor: style.bg, color: style.fg, fontWeight: 600 }}
                        />
                        <IconButton
                          size="small"
                          disabled={deletingId === doc.id}
                          onClick={() => handleDeleteDocument(doc.id, doc.title)}
                          sx={{ color: "#94a3b8", "&:hover": { color: "#dc2626" } }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Card>
        )}
        </Box>
      </Box>
    </>
  );
}
